import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { AdCreative } from '@marketing-milk/interfaces'
import { CreativeStatus } from 'components/creative-status/CreativeStatus'
import { CreativeMedia } from '../../../creatives/CreativeCard/CreativeMedia'
import { radius, SurfacePalette, type } from '../../tokens'
import { CommentIndicator } from '../../components/CommentIndicator'

interface Props {
  palette: SurfacePalette
  creatives?: AdCreative[]
  loading?: boolean
  /** True when the packet has no bound sprint to load creatives from. */
  noSprint?: boolean
  /**
   * When provided, clicking a card's media opens the live Facebook mobile-feed
   * preview — the surface that shows the full ad and hosts the comment / approve
   * / open-source actions. Enabled in both Edit and Present.
   */
  onPreview?: (creative: AdCreative) => void
  /**
   * When provided, hovering a card warms that creative's live preview into the
   * shared cache so the preview modal opens instantly. One creative at a time;
   * no-op without `onPreview`.
   */
  onPrefetch?: (creative: AdCreative) => void
}

const Muted = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>{children}</p>
)

/**
 * Shared grid of sprint creatives used by both the edit (`CreativeSection`) and
 * Present (`PresentCreativeSection`) hosts. Each card is its own `creative`
 * comment anchor keyed by the bare creative id (matching CreativeWizard so the
 * thread is shared) and reveals a per-card comment trigger on hover. The shared
 * click delegation in `useCommentAnchors` resolves a card trigger to the
 * nearest anchor ancestor — the card — leaving the section-chrome `section`
 * anchor (further up) untouched.
 */
export const CreativeCardList = ({
  palette,
  creatives,
  loading,
  noSprint,
  onPreview,
  onPrefetch,
}: Props) => {
  if (noSprint) return <Muted palette={palette}>This packet isn’t linked to a sprint.</Muted>
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: palette.textMuted }}>
        <CircularProgress size={18} style={{ color: palette.accent }} />
        <span style={{ fontSize: type.size.body }}>Loading creatives…</span>
      </div>
    )
  }
  if (!creatives || creatives.length === 0) {
    return <Muted palette={palette}>No creatives in this sprint yet.</Muted>
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 20,
      }}
    >
      {creatives.map(creative => (
        <div
          key={creative.id}
          className="group"
          data-comment-anchor-type="creative"
          data-comment-anchor-id={String(creative.id)}
          data-comment-label={creative.name}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: radius.md,
            border: `1px solid ${palette.hairline}`,
            background: palette.surface,
            overflow: 'hidden',
          }}
        >
          {/*
           * The card is a gallery tile: media is the click target that opens the
           * preview (where the full ad + comment / approve / open-source actions
           * live). The status chip and comment overlay below are SIBLINGS of this
           * element, not children, so clicking the comment trigger never fires
           * the preview handler and still bubbles to the `useCommentAnchors` root.
           */}
          <div
            role={onPreview ? 'button' : undefined}
            tabIndex={onPreview ? 0 : undefined}
            aria-label={onPreview ? `Preview ${creative.name}` : undefined}
            onClick={onPreview ? () => onPreview(creative) : undefined}
            onMouseEnter={onPrefetch ? () => onPrefetch(creative) : undefined}
            onKeyDown={
              onPreview
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onPreview(creative)
                    }
                  }
                : undefined
            }
            style={{
              position: 'relative',
              aspectRatio: '1 / 1',
              background: palette.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: onPreview ? 'pointer' : 'default',
            }}
          >
            {/*
             * Reuse the app-wide CreativeMedia thumbnail (image/video/carousel/
             * dynamic with broken-link fallbacks) rather than the single
             * denormalized s3Preview field, which is empty for most creatives.
             */}
            <CreativeMedia creative={creative} className="h-full w-full" />
            {onPreview && (
              // Faint hover overlay so the media reads as an interactive preview
              // target. group-hover is driven by the card wrapper's `group` class.
              <div
                className="opacity-0 group-hover:opacity-100"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                  fontFamily: type.family.sans,
                  fontSize: type.size.eyebrow,
                  fontWeight: type.weight.medium,
                  transition: 'opacity 120ms ease',
                  pointerEvents: 'none',
                }}
              >
                Preview ad
              </div>
            )}
          </div>

          {/* Status chip overlay (top-left), keeping its own CreativeStatus badge. */}
          <div style={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }}>
            <CreativeStatus creative={creative} className="shadow-sm" />
          </div>

          {/* Comment overlay (top-right), sibling of the media region. */}
          <div style={{ position: 'absolute', top: 10, right: 10 }}>
            <CommentIndicator
              palette={palette}
              label={creative.name}
              iconOnly
              style={{ background: 'rgba(0, 0, 0, 0.55)', color: '#fff', borderRadius: radius.sm }}
            />
          </div>

          <div style={{ padding: 14 }}>
            <span
              style={{
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                fontWeight: type.weight.medium,
                color: palette.text,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.35,
              }}
              title={creative.name}
            >
              {creative.name}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}
